import "./App.css"
import { useCallback, useEffect, useState } from "react"
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom"
import Login from "./components/Login/Login"
import Signup from "./components/Signup/Signup"
import Home from "./components/Home/Home"
import Skills from "./components/Skills/Skills"
import Orders from "./components/Orders/Orders"
import NavigationBar from "./components/NavigationBar/NavigationBar"
import NavigationListener from "./components/hooks/Utils/NavigationListener"
import Business from "./components/Business/Business"
import BuyCredit from "./components/Credit/BuyCredit"
import Activity from "./components/Activity/Activities"
import Banner from "./components/UI/Banner/Banner"
import BusinessInfoForm from "./components/Business/BusinessInfoForm"
import BusinessPage from "./components/Business/BusinessPage"
import Groups from "./components/Groups/Groups"
import "./components/hooks/Utils/i18n"
import ProfilePage from "./components/Profile/ProfilePage"
import useFavicon from "./components/hooks/Utils/useFavIcon"
import i18n from "i18next"
import ResetPasswordPage from "./components/ResetPassword/ResetPasswordPage"
import SuccessPage from "./components/Credit/SuccessPage"
import { Analytics } from "@vercel/analytics/react"
import LoadingPage from "./components/LoadingPage/LoadingPage"
import { Helmet, HelmetProvider } from "react-helmet-async"
import Marketplace from "./components/Marketplace/Marketplace"
import KobiCV from "./components/KobiCV/KobiCV"
import MyIsland from "./components/MyIsland/MyIsland"
import SocketManager from "./components/hooks/Utils/Socket/SocketManager"
import Skill from "./components/Skills/Skill"
import DemandAnalyticsPage from "./components/Demand/DemandAnalyticsPage"
import PrivacyPolicy from "./components/PrivacyPolicy/PrivacyPolicy"
import TermsOfService from "./components/Terms/TermsOfService"
import CookiesDisclaimer from "./components/CookiesDisclaimer/CookiesDisclaimer"
import { createTheme, ThemeProvider } from "@mui/material/styles"

export const lightTheme = createTheme({
  palette: {
    mode: "light",
  },
  typography: {
    h1: { fontSize: `calc(6rem * var(--app-font-scale, 1))` },
    h2: { fontSize: `calc(3.75rem * var(--app-font-scale, 1))` },
    h3: { fontSize: `calc(3rem * var(--app-font-scale, 1))` },
    h4: { fontSize: `calc(2.125rem * var(--app-font-scale, 1))` },
    h5: { fontSize: `calc(1.5rem * var(--app-font-scale, 1))` },
    h6: { fontSize: `calc(1.25rem * var(--app-font-scale, 1))` },
    body1: { fontSize: `calc(1rem * var(--app-font-scale, 1))` },
    body2: { fontSize: `calc(0.875rem * var(--app-font-scale, 1))` },
    // המשך לפי הצורך
  },
})

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
  typography: {
    h1: { fontSize: `calc(6rem * var(--app-font-scale, 1))` },
    h2: { fontSize: `calc(3.75rem * var(--app-font-scale, 1))` },
    h3: { fontSize: `calc(3rem * var(--app-font-scale, 1))` },
    h4: { fontSize: `calc(2.125rem * var(--app-font-scale, 1))` },
    h5: { fontSize: `calc(1.5rem * var(--app-font-scale, 1))` },
    h6: { fontSize: `calc(1.25rem * var(--app-font-scale, 1))` },
    body1: { fontSize: `calc(1rem * var(--app-font-scale, 1))` },
    body2: { fontSize: `calc(0.875rem * var(--app-font-scale, 1))` },
    // המשך לפי הצורך
  },
})

function App() {
  const [showBanner, setShowBanner] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(null)
  const [isRefreshed, setIsRefreshed] = useState(null)
  useFavicon()
  const searchParams = new URLSearchParams(window.location.search)
  const isFromRNQuery = searchParams.get("fromRN") === "true"

  if (isFromRNQuery) {
    localStorage.setItem("fromRN", "true")
  }
  useEffect(() => {
    tryRefresh()
      .then(() => {
        checkAuth()
      })
      .catch((err) => {})
  }, [])
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBanner(false)
    }, 1500)

    // language change
    if (i18n.language === "he") {
      i18n.changeLanguage("he")
    } else {
      i18n.changeLanguage("en")
    }

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Check authentication status on initial load
    // checkAuth()

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        tryRefresh()
          .then(() => {
            checkAuth()
          })
          .catch((err) => {})
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const tryRefresh = async () => {
    try {
      await fetch("https://sharie.vercel.app/api/users/refresh-token", {
        method: "POST",
        credentials: "include",
      })
      checkAuth()
    } catch (err) {}
    setIsRefreshed(true)
  }

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch("https://sharie.vercel.app/api/users/check-auth", {
        credentials: "include",
      })
      if (res.ok) {
        setIsAuthenticated(true)
      } else {
        setIsAuthenticated(false)
      }
    } catch (err) {
      setIsAuthenticated(false)
    }
  })

  return (
    <HelmetProvider>
      <Helmet>
        <title>Sharie</title>
        <meta
          name='description'
          content='The best place to share your skills and talent!Teach and learn new things!'
        />
      </Helmet>
      <SocketManager>
        <BrowserRouter>
          {showBanner && <Banner />}

          <NavigationListener />
          {/* <ThemeProvider theme={lightTheme}> */}
          {isAuthenticated !== null && isRefreshed && (
            <AppRoutes isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />
          )}
          {/* </ThemeProvider> */}
          <Analytics />
        </BrowserRouter>
      </SocketManager>
    </HelmetProvider>
  )
}

export default App

const AppRoutes = ({ isAuthenticated, setIsAuthenticated }) => {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (
      location.pathname.startsWith("/reset-password") ||
      location.pathname === "/signup" ||
      isAuthenticated === null
    )
      return

    isAuthenticated && navigate(localStorage.getItem("currentPath"))
  }, [isAuthenticated])

  useEffect(() => {
    isAuthenticated &&
      !location.pathname.startsWith("/reset-password") &&
      localStorage.setItem("currentPath", location.pathname)
  }, [location.pathname])

  const PrivateRoute = ({ children }) => {
    return isAuthenticated || localStorage.getItem("guest") === "true" ? (
      children
    ) : (
      <Navigate to='/' />
    )
  }

  return (
    <>
      {isAuthenticated &&
        location.pathname !== "/" &&
        location.pathname !== "/login" &&
        location.pathname !== "/signup" &&
        !location.pathname.startsWith("/reset-password") && (
          <NavigationBar
            isAuthenticated={isAuthenticated}
            setIsAuthenticated={setIsAuthenticated}
          />
        )}
      <Routes>
        <Route path='/' element={<LoadingPage />} />
        <Route path='/login' element={<Login setIsAuthenticated={setIsAuthenticated} />} />
        <Route path='/signup' element={<Signup />} />
        <Route
          path='/home'
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path='/skills'
          element={
            <PrivateRoute>
              <Skills />
            </PrivateRoute>
          }
        />
        <Route
          path='/orders'
          element={
            <PrivateRoute>
              <Orders />
            </PrivateRoute>
          }
        />
        <Route
          path='/profile'
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        <Route
          path='/business'
          element={
            <PrivateRoute>
              <Business />
            </PrivateRoute>
          }
        />
        <Route
          path='/buycredit'
          element={
            <PrivateRoute>
              <BuyCredit />
            </PrivateRoute>
          }
        />
        <Route
          path='/activity'
          element={
            <PrivateRoute>
              <Activity />
            </PrivateRoute>
          }
        />
        <Route
          path='/businessinfo'
          element={
            <PrivateRoute>
              <BusinessInfoForm />
            </PrivateRoute>
          }
        />
        <Route
          path='/businesspage'
          element={
            <PrivateRoute>
              <BusinessPage />
            </PrivateRoute>
          }
        />
        <Route
          path='/groups'
          element={
            <PrivateRoute>
              <Groups />
            </PrivateRoute>
          }
        />
        <Route
          path='/marketplace'
          element={
            <PrivateRoute>
              <Marketplace />
            </PrivateRoute>
          }
        />
        <Route
          path='/myisland'
          element={
            <PrivateRoute>
              <MyIsland setIsAuthenticated={setIsAuthenticated} />
            </PrivateRoute>
          }
        />
        <Route
          path='/demands'
          element={
            <PrivateRoute>
              <DemandAnalyticsPage />
            </PrivateRoute>
          }
        />
        <Route
          path='/privacy-policy'
          element={
            <PrivateRoute>
              <PrivacyPolicy />
            </PrivateRoute>
          }
        />
        <Route
          path='/terms-of-service'
          element={
            <PrivateRoute>
              <TermsOfService />
            </PrivateRoute>
          }
        />
        <Route
          path='/cookies'
          element={
            <PrivateRoute>
              <CookiesDisclaimer />
            </PrivateRoute>
          }
        />
        <Route path='/kobicv' element={<KobiCV />} />
        <Route path='/reset-password/:token' element={<ResetPasswordPage />} />
        <Route path='/success/:invoice_id' element={<SuccessPage />} />
        <Route path='/skill/:id' element={<Skill />} />
      </Routes>
    </>
  )
}
